/* ===== Nucleo Top Navigation ===== */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, Bell, Building, LogOut, User, ChevronDown, Check, Sun, Moon } from 'lucide-react'
import { ROLE_LABELS } from '@zetyar/domain'
import { useApp } from '@/presentation/providers/useApp'
import { Avatar } from '@/presentation/components/ui/Avatar'
import { useTheme } from '@/presentation/providers/useTheme'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webNotificationRepository } from '@/infrastructure/repositories/notifications'

const NOTIFICATION_POLL_INTERVAL_MS = 20_000

interface NavbarProps {
  onOpenMobileMenu: () => void
}

export function Navbar({ onOpenMobileMenu }: NavbarProps) {
  const { profile, currentOrganization, currentMembership, organizations, selectOrganization, signOut, user } = useApp()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [showOrgDropdown, setShowOrgDropdown] = useState(false)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!user || !profile || !currentOrganization) {
      setUnreadCount(0)
      return
    }

    let isActive = true
    const context = createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })

    const poll = async () => {
      try {
        const count = await webNotificationRepository.getUnreadCount(context)
        if (isActive) setUnreadCount(count)
      } catch (error) {
        console.error('Error al revisar notificaciones no leidas:', error)
      }
    }

    void poll()
    const intervalId = setInterval(() => void poll(), NOTIFICATION_POLL_INTERVAL_MS)

    const handleRead = () => {
      void poll()
    }
    window.addEventListener('notifications-read', handleRead)

    return () => {
      isActive = false
      clearInterval(intervalId)
      window.removeEventListener('notifications-read', handleRead)
    }
  }, [user, profile, currentOrganization, currentMembership])

  const roleLabel = profile?.isSuperAdmin
    ? 'Super admin'
    : currentMembership?.roles.length
      ? currentMembership.roles.map((role) => ROLE_LABELS[role] ?? role).join(', ')
      : null

  const handleSignOut = async () => {
    await signOut()
    setShowUserDropdown(false)
    navigate('/login', { replace: true })
  }

  return (
    <header className="sticky top-0 z-30 flex min-h-16 items-center gap-4 border-b border-transparent bg-[#f4f7fb]/80 px-4 py-3 backdrop-blur-[20px] dark:bg-neutral-950/80 md:px-7">
      <button
        onClick={onOpenMobileMenu}
        className="grid h-[42px] w-[42px] place-items-center rounded-[13px] border border-neutral-200 bg-white text-neutral-500 hover:-translate-y-0.5 hover:bg-neutral-50 hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white lg:hidden"
        aria-label="Abrir menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="relative">
        <button
          onClick={() => setShowOrgDropdown(!showOrgDropdown)}
          className="flex h-[42px] items-center gap-2.5 rounded-[13px] border border-neutral-200 bg-white px-3.5 text-left hover:-translate-y-0.5 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800"
        >
          {currentOrganization?.logoUrl ? (
            <img
              src={currentOrganization.logoUrl}
              alt={currentOrganization.name}
              className="h-8 w-8 rounded-[11px] border border-neutral-200 bg-white object-contain p-0.5 dark:border-neutral-700 dark:bg-neutral-900"
            />
          ) : (
            <div className="grid h-8 w-8 place-items-center rounded-[11px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-200">
              <Building className="h-4 w-4" />
            </div>
          )}
          <div className="hidden min-w-0 sm:block">
            <span className="block max-w-[220px] truncate text-sm font-extrabold leading-tight text-neutral-900 dark:text-white">
              {currentOrganization?.name || 'Seleccionar organizacion'}
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-wide leading-tight text-neutral-500 dark:text-neutral-400">
              Zetyar Nexo
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-neutral-400" />
        </button>

        {showOrgDropdown && (
          <div
            className="absolute left-0 z-50 mt-2 w-72 animate-scale-in rounded-[18px] border border-neutral-200 bg-white py-2 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900"
            onClick={() => setShowOrgDropdown(false)}
          >
            <div className="px-4 py-2 text-[10px] font-bold uppercase text-neutral-400">
              Mis organizaciones
            </div>
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => selectOrganization(org.id)}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
                  org.id === currentOrganization?.id
                    ? 'bg-primary-50/80 font-bold text-primary-600 dark:bg-primary-500/15 dark:text-primary-200'
                    : 'text-neutral-700 dark:text-neutral-300'
                }`}
              >
                <span className="truncate">{org.name}</span>
                {org.id === currentOrganization?.id && (
                  <Check className="h-4 w-4 shrink-0 text-primary-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        <button
          onClick={toggleTheme}
          className="grid h-[42px] w-[42px] place-items-center rounded-[13px] border border-neutral-200 bg-white text-neutral-500 hover:-translate-y-0.5 hover:bg-neutral-50 hover:text-primary-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-primary-200"
          title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          aria-label="Cambiar tema de interfaz"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5 text-warning-500" /> : <Moon className="h-5 w-5" />}
        </button>

        <button
          onClick={() => navigate('/notifications')}
          className="relative grid h-[42px] w-[42px] place-items-center rounded-[13px] border border-neutral-200 bg-white text-neutral-500 hover:-translate-y-0.5 hover:bg-neutral-50 hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
          aria-label={unreadCount > 0 ? `Notificaciones, ${unreadCount} sin leer` : 'Notificaciones'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger-500 ring-2 ring-white dark:ring-neutral-900" />
          )}
        </button>

        <div className="relative">
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex h-[42px] items-center gap-2.5 rounded-full border border-transparent px-1.5 pr-2 hover:border-neutral-200 hover:bg-white dark:hover:border-neutral-800 dark:hover:bg-neutral-900"
          >
            <Avatar src={profile?.avatarUrl} name={profile?.displayName || 'Usuario'} size="sm" />
            <span className="hidden flex-col items-start leading-tight md:flex">
              <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                {profile?.displayName}
              </span>
              {roleLabel && (
                <span className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500">{roleLabel}</span>
              )}
            </span>
            <ChevronDown className="hidden h-4 w-4 text-neutral-400 md:block" />
          </button>

          {showUserDropdown && (
            <div
              className="absolute right-0 z-50 mt-2 w-60 animate-scale-in rounded-[18px] border border-neutral-200 bg-white py-2 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900"
              onClick={() => setShowUserDropdown(false)}
            >
              <div className="border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
                <p className="truncate text-sm font-bold text-neutral-900 dark:text-white">
                  {profile?.displayName}
                </p>
                <p className="mt-0.5 truncate text-xs text-neutral-400">{profile?.email}</p>
                {roleLabel && (
                  <p className="mt-1 truncate text-[11px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
                    {roleLabel}
                  </p>
                )}
              </div>

              <a
                href="#/profile"
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                <User className="h-4 w-4 text-neutral-400" />
                <span>Mi perfil</span>
              </a>

              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="mt-1 flex w-full items-center gap-2.5 border-t border-neutral-100 px-4 py-2.5 text-left text-sm text-danger-600 transition-colors hover:bg-danger-50 dark:border-neutral-800 dark:text-danger-200 dark:hover:bg-danger-500/10"
              >
                <LogOut className="h-4 w-4 text-danger-500" />
                <span>Cerrar sesion</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
